import { apiClient } from '../shared/utils/api';
import type { LoginCredentials, AuthResponse, User } from '../types/auth';

// Token 管理
export const TokenStorage = {
    getAccessToken: (): string | null => {
        return localStorage.getItem('accessToken');
    },

    setAccessToken: (token: string): void => {
        localStorage.setItem('accessToken', token);
    },

    getRefreshToken: (): string | null => {
        return localStorage.getItem('refreshToken');
    },

    setRefreshToken: (token: string): void => {
        localStorage.setItem('refreshToken', token);
    },

    clearTokens: (): void => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tenantId');
    },

    getUser: (): User | null => {
        const userJson = localStorage.getItem('user');
        return userJson ? JSON.parse(userJson) : null;
    },

    setUser: (user: User): void => {
        localStorage.setItem('user', JSON.stringify(user));
    },

    clearUser: (): void => {
        localStorage.removeItem('user');
    },

    getTenantId: (): string | null => {
        return localStorage.getItem('tenantId');
    },

    setTenantId: (tenantId: string): void => {
        localStorage.setItem('tenantId', tenantId);
    },

    clearTenantId: (): void => {
        localStorage.removeItem('tenantId');
    },
};

// Auth API
export const AuthAPI = {
    // 登入
    login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
        const response = await apiClient.post<AuthResponse>('/auth/login', credentials);
        return response;
    },

    // 登出
    logout: async (): Promise<void> => {
        const refreshToken = TokenStorage.getRefreshToken();
        if (refreshToken) {
            await apiClient.post('/auth/logout', { refreshToken });
        }
    },

    // 刷新 Token
    refresh: async (refreshToken: string): Promise<AuthResponse> => {
        const response = await apiClient.post<AuthResponse>('/auth/refresh', { refreshToken });
        return response;
    },

    // 驗證當前 session
    verify: async (): Promise<User> => {
        const response = await apiClient.get<User>('/auth/verify');
        return response;
    },

    // 修改密碼 (使用者自己)
    changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
        await apiClient.post('/auth/change-password', {
            currentPassword,
            newPassword,
        });
    },

    // 管理員生成密碼重設連結
    adminResetPassword: async (
        userId: string
    ): Promise<{ message: string; resetUrl: string; expiresIn: string }> => {
        const response = await apiClient.post<{
            message: string;
            resetUrl: string;
            expiresIn: string;
        }>(`/auth/admin-reset-password/${userId}`, {});
        return response;
    },

    // 驗證密碼重設 token 並取得使用者資訊 (公開端點)
    validateResetToken: async (token: string): Promise<{ username: string; name: string }> => {
        const response = await apiClient.get<{ username: string; name: string }>(
            `/auth/validate-reset-token?token=${encodeURIComponent(token)}`
        );
        return response;
    },

    // 使用 token 重設密碼 (公開端點，不需要認證)
    resetPasswordWithToken: async (
        token: string,
        newPassword: string
    ): Promise<{ message: string }> => {
        const response = await apiClient.post<{ message: string }>(
            '/auth/reset-password-with-token',
            { token, newPassword }
        );
        return response;
    },

    // 忘記密碼 - 發送重設信 (公開端點)
    forgotPassword: async (email: string): Promise<{ message: string }> => {
        const response = await apiClient.post<{ message: string }>('/auth/forgot-password', {
            email,
        });
        return response;
    },
};
